import Base from "./Base.js";
import Events from "./mixin/Events.js";
import DomHelper from "./helper/DomHelper.js";
import BrowserHelper from "./helper/BrowserHelper.js";
import Rectangle from "./helper/util/Rectangle.js";
const activeTouches = /* @__PURE__ */ new Map(), longpressMoveThreshold = 5, isFloatingWidget = (w) => w.floating, longPressCancelEvents = {
  touchend: 1,
  pointerup: 1
}, ignoreModifierKeys = {
  Meta: 1,
  Control: 1,
  Alt: 1
}, onPointerUp = (event) => {
  if ((currentPointerDown == null ? void 0 : currentPointerDown.pointerId) === event.pointerId) {
    currentPointerDown = null;
  }
}, trackTouches = (event) => {
  const { touches } = event;
  activeTouches.clear();
  if (touches) {
    for (let i = 0; i < touches.length; i++) {
      const touch = touches.item(i);
      activeTouches.set(touch.identifier, [touch, event]);
    }
  }
}, GlobalEvents = new class GlobalEventsHandler extends Base.mixin(Events) {
  suspendFocusEvents() {
    focusEventsSuspended = true;
  }
  resumeFocusEvents() {
    focusEventsSuspended = false;
  }
  setupFocusListenersOnce(rootElement, EventHelper) {
    if (rootElement && !GlobalEvents.observedElements.has(rootElement)) {
      GlobalEvents.setupFocusListeners(rootElement, EventHelper);
      GlobalEvents.observedElements.add(rootElement);
    }
  }
  detachFocusListeners(rootElement) {
    if (rootElement.$globalEventsDetacher) {
      rootElement.$globalEventsDetacher();
      rootElement.$globalEventsDetacher = null;
      GlobalEvents.observedElements.delete(rootElement);
    }
  }
  // This is imported by EventHelper and that makes the call to set up the listeners
  // `detach` argument is required to not setup more listeners than we need to. In case of salesforce we include floatroot
  // inside the webcomponent element and thus don't need default listeners on document. In regular webcomponents demo we
  // don't need to do it, because with multiple components on one page that would force us to make more complex lookups.
  setupFocusListeners(element = document, EventHelper, detach = false) {
    let lastPinchPos, pinchCenter, pinchTarget;
    const listeners = {
      element,
      fromGlobal: true,
      touchstart(touchstart) {
        const { touches: ct } = touchstart;
        trackTouches(touchstart);
        if (ct.length === 2 && ct[0].target.closest(".b-widget") && ct[1].target.closest(".b-widget")) {
          lastPinchPos = [ct[0], ct[1]];
          pinchCenter = [
            (lastPinchPos[0].clientX + lastPinchPos[1].clientX) / 2,
            (lastPinchPos[0].clientY + lastPinchPos[1].clientY) / 2
          ];
          pinchTarget = DomHelper.elementFromPoint(pinchCenter[0], pinchCenter[1]);
          return;
        }
        if (!globalTouchStart && touchstart.changedTouches.length === 1) {
          globalTouchStart = touchstart.changedTouches[0];
          if (!BrowserHelper.isAndroid) {
            const onMoveOrPointerUp = ({ clientX, clientY, type }) => {
              if (longPressCancelEvents[type] || !globalTouchStart || Math.max(Math.abs(clientX - globalTouchStart.clientX), Math.abs(clientY - globalTouchStart.clientY)) > longpressMoveThreshold) {
                contextMenuTouchId = null;
                touchMoveRemover();
                clearTimeout(tapholdTimer);
              }
            }, touchMoveRemover = EventHelper.on({
              element: document,
              touchmove: onMoveOrPointerUp,
              touchend: onMoveOrPointerUp,
              pointermove: onMoveOrPointerUp,
              pointerup: onMoveOrPointerUp,
              capture: true
            }), tapholdTimer = setTimeout(() => {
              contextMenuTouchId = globalTouchStart.identifier;
              touchMoveRemover();
              touchstart.target.dispatchEvent(new MouseEvent("contextmenu", EventHelper.copyEvent({}, touchstart)));
            }, EventHelper.longPressTime);
          }
        } else {
          globalTouchStart = null;
        }
      },
      touchmove: {
        passive: false,
        handler: (touchMove) => {
          const { touches: ct } = touchMove;
          if (ct.length === 2 && lastPinchPos) {
            const pinchPos = [ct[0], ct[1]], delta = EventHelper.getDistanceBetween(lastPinchPos[0], lastPinchPos[1]) - EventHelper.getDistanceBetween(pinchPos[0], pinchPos[1]), deltaX = Math.abs(lastPinchPos[0].clientX - lastPinchPos[1].clientX) - Math.abs(pinchPos[0].clientX - pinchPos[1].clientX), deltaY = Math.abs(lastPinchPos[0].clientY - lastPinchPos[1].clientY) - Math.abs(pinchPos[0].clientY - pinchPos[1].clientY);
            if (Math.abs(delta) > 2) {
              const pinch = {
                clientX: pinchCenter[0],
                clientY: pinchCenter[1],
                bubbles: true
              };
              if (GlobalEvents.Widget.convertPinchToMousewheel) {
                touchMove.preventDefault();
                touchMove.stopImmediatePropagation();
                const e2 = new MouseEvent("wheel", { ...pinch, ctrlKey: true });
                e2.deltaY = deltaY;
                e2.deltaX = deltaX;
                pinchTarget.dispatchEvent(e2);
              }
              const e = new MouseEvent("bryntum-pinch", pinch);
              e.deltaY = deltaY;
              e.deltaX = deltaX;
              e.touchMove = touchMove;
              pinchTarget.dispatchEvent(e);
            }
            lastPinchPos = pinchPos;
          }
        }
      },
      // Just this one has to be passive: false so that we are allowed to preventDefault
      // if we are part of a contextmenu longpress emulation. Otherwise the gesture will
      // proceed to cause a mousedown event.
      touchend: {
        passive: false,
        handler: (event) => {
          trackTouches(event);
          if (event.touches.length !== 2) {
            lastPinchPos = null;
          }
          if (globalTouchStart) {
            if (event.changedTouches[0].identifier === contextMenuTouchId) {
              event.stopImmediatePropagation();
              if (event.cancelable !== false) {
                event.preventDefault();
              }
            } else if (event.changedTouches.length === 1 && event.changedTouches[0].identifier === globalTouchStart.identifier) {
              GlobalEvents.trigger("globaltap", { event });
            }
            globalTouchStart = null;
          }
        }
      },
      touchcancel: {
        passive: false,
        handler: trackTouches
      },
      mousedown: {
        handler: (event) => {
          lastInteractionType = "mouse";
          if (!globalTouchStart) {
            GlobalEvents.trigger("globaltap", { event });
          }
          currentMouseDown = event;
          const hasModifierKey = event.ctrlKey || event.altKey || event.shiftKey || event.metaKey;
          if (!currentKeyDown && hasModifierKey) {
            currentKeyDown = new KeyboardEvent("keydown", {
              key: event.ctrlKey ? "Control" : event.shiftKey ? "Shift" : event.altKey ? "Alt" : "Meta",
              ctrlKey: event.ctrlKey,
              altKey: event.altKey,
              shiftKey: event.shiftKey,
              metaKey: event.metaKey
            });
          } else if (currentKeyDown && !hasModifierKey) {
            currentKeyDown = null;
          }
        },
        passive: false
      },
      mouseup() {
        currentMouseDown = null;
      },
      pointerdown: {
        passive: false,
        handler: (event) => {
          currentPointerDown = event;
          DomHelper.setUsingKeyboard(element, false);
        }
      },
      pointerup: {
        passive: false,
        handler: onPointerUp
      },
      pointercancel: {
        passive: false,
        handler: onPointerUp
      },
      keydown(ev) {
        const { key, target } = ev;
        lastInteractionType = "key";
        currentKeyDown = ev;
        currentOverElement = target;
        if (!ignoreModifierKeys[key]) {
          if (element.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
            for (const node of element.children) {
              if (node.matches(".b-outer")) {
                DomHelper.setUsingKeyboard(node, true);
              }
            }
          } else {
            DomHelper.setUsingKeyboard(element, true);
          }
          if (key === "Escape") {
            GlobalEvents.Widget.queryAll((w) => w.isTooltip && w.isVisible).forEach((w) => w.hide());
          }
        }
      },
      mouseenter(ev) {
        currentOverElement = ev.target;
      },
      keypress() {
        lastInteractionType = "key";
      },
      keyup() {
        currentKeyDown = null;
      },
      focusin(focusin) {
        var _a;
        const { Widget } = GlobalEvents, { target } = focusin;
        if ((target == null ? void 0 : target.shadowRoot) || (target == null ? void 0 : target._shadowRoot)) {
          return;
        }
        if (target && Widget.accessibility) {
          const inner = target.firstElementChild || target, rect = Rectangle.from(target), center = rect.center;
          (DomHelper.childFromPoint(inner, rect.width / 2, rect.height / 2) || inner).dispatchEvent(new PointerEvent("pointerover", {
            bubbles: true,
            clientX: center.x,
            clientY: center.y,
            pointerType: "focus"
          }));
        }
        if (focusEventsSuspended || target && !target.isConnected) {
          return;
        }
        const fromElement = !focusin.relatedTarget ? null : focusin.relatedTarget instanceof HTMLElement ? focusin.relatedTarget : document.body, toElement = target || document.body, fromWidget = Widget.fromElement(fromElement), toWidget = Widget.fromElement(toElement), commonAncestor = DomHelper.getCommonAncestor(fromWidget, toWidget), backwards = Boolean(fromElement && toElement.compareDocumentPosition(fromElement) & 4), topVisibleModal = Widget.query(isTopVisibleModal);
        let currentFocus = null;
        if (toElement && toElement !== document.body) {
          currentFocus = DomHelper.getActiveElement(toElement);
        } else {
          currentFocus = DomHelper.getActiveElement(document);
        }
        if (topVisibleModal && !topVisibleModal._isRevertingFocus) {
          if (!toWidget || !topVisibleModal.owns(toWidget) && !(topVisibleModal.element.compareDocumentPosition(toWidget.element) & 4 && toWidget.up(isFloatingWidget))) {
            return topVisibleModal.focus();
          }
        }
        let event = createWidgetEvent("focusout", fromElement, target, fromWidget, toWidget, backwards);
        for (let targetWidget = fromWidget, owner; targetWidget && targetWidget !== commonAncestor; targetWidget = owner) {
          owner = targetWidget.owner;
          if (!targetWidget.isDestroying && targetWidget.onFocusOut) {
            targetWidget.onFocusOut(event);
            if (target && currentFocus !== DomHelper.getActiveElement(target)) {
              return;
            }
          }
        }
        if (commonAncestor && target === commonAncestor.element) {
          if (!commonAncestor.isDestroying && DomHelper.getActiveElement(commonAncestor) === toElement && commonAncestor.focusElement && commonAncestor.focusElement !== commonAncestor.element) {
            if (!commonAncestor.element.contains(currentFocus) || commonAncestor.focusDescendant) {
              commonAncestor.setTimeout(() => {
                var _a2;
                return (_a2 = commonAncestor.focus) == null ? void 0 : _a2.call(commonAncestor);
              }, 0);
            }
          }
        } else {
          event = createWidgetEvent("focusin", toElement, fromElement, fromWidget, toWidget, backwards);
          for (let targetWidget = toWidget; targetWidget && targetWidget !== commonAncestor; targetWidget = targetWidget.owner) {
            if (!targetWidget.isDestroying) {
              (_a = targetWidget.onFocusIn) == null ? void 0 : _a.call(targetWidget, event);
            }
          }
        }
        const commonAncestorEl = DomHelper.getCommonAncestor((fromElement == null ? void 0 : fromElement.nodeType) === Element.ELEMENT_NODE ? fromElement : null, toElement) || toElement.parentNode;
        if (commonAncestorEl) {
          event = createWidgetEvent("focusmove", toElement, fromElement, fromWidget, toWidget, backwards, { bubbles: true });
          commonAncestorEl.dispatchEvent(event);
        }
      },
      focusout(focusout) {
        if (focusEventsSuspended) {
          return;
        }
        if (!focusout.relatedTarget || !GlobalEvents.Widget.fromElement(focusout.relatedTarget)) {
          const target = focusout.relatedTarget && focusout.relatedTarget instanceof HTMLElement ? focusout.relatedTarget : null;
          listeners.focusin({
            target,
            relatedTarget: focusout.target
          });
          currentKeyDown = currentMouseDown = null;
        }
      },
      // This will clear keydown and mousedown status on window blur
      blur: {
        element: globalThis,
        handler(event) {
          if (event.target === globalThis) {
            currentKeyDown = null;
            currentMouseDown = null;
          }
        }
      },
      capture: true,
      passive: true
    };
    detach && (detacher == null ? void 0 : detacher());
    detacher = this.detachEvents = element.$globalEventsDetacher = EventHelper.on(listeners);
  }
  get activeTouches() {
    return Array.from(activeTouches.values());
  }
  get hasActiveTouches() {
    return activeTouches.size > 0;
  }
  get lastInteractionType() {
    return lastInteractionType;
  }
  get shiftKeyDown() {
    return Boolean(currentKeyDown == null ? void 0 : currentKeyDown.shiftKey);
  }
  get ctrlKeyDown() {
    return Boolean((currentKeyDown == null ? void 0 : currentKeyDown.ctrlKey) || (currentKeyDown == null ? void 0 : currentKeyDown.metaKey));
  }
  get altKeyDown() {
    return Boolean(currentKeyDown == null ? void 0 : currentKeyDown.altKey);
  }
  get currentOverElement() {
    return currentOverElement;
  }
  isKeyDown(key) {
    return !key ? Boolean(currentKeyDown) : (currentKeyDown == null ? void 0 : currentKeyDown.key) === key || currentKeyDown[(key == null ? void 0 : key.toLowerCase()) + "Key"] === true;
  }
  isMouseDown(button = 0) {
    return (currentMouseDown == null ? void 0 : currentMouseDown.button) === button;
  }
  get currentMouseDown() {
    return currentMouseDown;
  }
  set currentMouseDown(m) {
    currentMouseDown = m;
  }
  get currentPointerDown() {
    return currentPointerDown;
  }
  set currentPointerDown(p) {
    currentPointerDown = p;
  }
  get currentTouch() {
    return globalTouchStart;
  }
  set currentTouch(t) {
    globalTouchStart = t;
  }
  get currentKeyDown() {
    return currentKeyDown;
  }
  set currentKeyDown(k) {
    currentKeyDown = k;
  }
}(), isTopVisibleModal = (w) => w.isTopModal;
GlobalEvents.observedElements = /* @__PURE__ */ new Set();
let globalTouchStart, contextMenuTouchId, focusEventsSuspended = false, lastInteractionType, currentKeyDown, currentMouseDown, currentPointerDown, currentOverElement, detacher;
function createWidgetEvent(eventName, target, relatedTarget, fromWidget, toWidget, backwards, options) {
  const result = new CustomEvent(eventName, options);
  Object.defineProperty(result, "_target", {
    get() {
      return target;
    }
  });
  Object.defineProperty(result, "relatedTarget", {
    get() {
      return relatedTarget;
    }
  });
  result.fromWidget = fromWidget;
  result.toWidget = toWidget;
  result.backwards = backwards;
  return result;
}
var GlobalEvents_default = GlobalEvents;
export {
  GlobalEvents_default as default
};
